import React, { type SyntheticEvent } from 'react';
import { object, string, array, boolean } from 'yup';
import { Box, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import type { DeclarationValues, DeclarationType } from './types';
import { PAGE_TEXT } from '../../lang';
import { NominationDeclaration } from '../../../graphql/NominationForm/Declaration/types';
import { Accordion, Button, Checkbox } from '../../../../../components';

type DeclarationProps = {
  initialValues: DeclarationValues[];
  isViewMode: boolean;
  onSubmit: (values: DeclarationValues[]) => void;
  declarationType?: DeclarationType;
};

const pageText = PAGE_TEXT.DECLARATION;

export const DeclarationLabels: Record<NominationDeclaration, string> = {
  [NominationDeclaration.NO_INTEREST_CONFLICT]: pageText.FORALL.DECLARATION_1,
  [NominationDeclaration.NOT_REGISTER_HIGHER_DEGREE]:
    pageText.FORALL.DECLARATION_2,
  [NominationDeclaration.INFORMATION_TRUE]: pageText.FORALL.DECLARATION_3,
  [NominationDeclaration.APPLICANT_MEET_HKPFS_THRESHOLD]:
    pageText.FORHKPFS.DECLARATION_4,
  [NominationDeclaration.ADMISSION_USING_EXTERNAL_OR_DEA]:
    pageText.FOREXTERNALORDEA.DECLARATION_5,
  [NominationDeclaration.NO_RESEARCH_COLLABORATION_WITH_PARTNER_CHIEF_SUPERVISOR]:
    pageText.FORDUAL_PHD.DECLARATION_6,
};

export const DeclarationBox: React.FC<DeclarationProps> = props => {
  const { initialValues, isViewMode, onSubmit } = props;
  const formik = useFormik<DeclarationValues[]>({
    initialValues,
    validationSchema: array().of(
      object().shape({
        content: string().required('Required'),
        confirmed: boolean().when(['content'], {
          is: (content: string) => {
            return content !== NominationDeclaration.NO_INTEREST_CONFLICT;
          },
          then: schema => schema.required().oneOf([true]),
          otherwise: schema => schema.notRequired(),
        }),
        justification: string().when(['content', 'confirmed'], {
          is: (content: string, confirmed: boolean) => {
            return (
              content === NominationDeclaration.NO_INTEREST_CONFLICT &&
              !confirmed
            );
          },

          then: schema => schema.required('Required'),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    ),
    onSubmit,
  });

  const touched = formik.touched;
  const errors = formik.errors;

  const handleCheckboxChange =
    (index: number) =>
    async (event: SyntheticEvent<Element, Event>, checked: boolean) => {
      if (index === 0 && checked) {
        await formik.setFieldValue(`[${index}].justification`, '');
      }
      await formik.setFieldValue(`[${index}].confirmed`, checked);
    };

  return (
    <div className="mt-[1rem]">
      <Accordion
        style={{ width: '100%' }}
        title={pageText.TITLE}
        defaultExpanded
        disableExpand
        details={
          <form onSubmit={formik.handleSubmit} noValidate>
            <Stack className="declaration-box">
              <Box padding={2}>
                {initialValues.map((label, index) => {
                  const contentLabel = DeclarationLabels[label.content];

                  return (
                    <div key={index}>
                      <Checkbox
                        name={`[${index}].confirmed`}
                        label={
                          <div
                            className="mt-2"
                            dangerouslySetInnerHTML={{ __html: contentLabel }}
                          />
                        }
                        disabled={isViewMode}
                        sx={{
                          alignItems: 'flex-start',
                        }}
                        value={formik.values[index]?.confirmed}
                        onChange={handleCheckboxChange(index)}
                        onBlur={formik.handleBlur}
                        error={
                          Boolean(touched[index]?.confirmed) &&
                          Boolean(errors[index]?.confirmed)
                        }
                      />
                      {!formik.values[0].confirmed && index === 0 && (
                        <Box className="p-6">
                          <TextField
                            disabled={isViewMode}
                            name={`[0].justification`}
                            className="w-full"
                            label={pageText.FORALL.DECLARATION_1_SUB}
                            onChange={formik.handleChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={formik.handleBlur}
                            value={formik.values?.[0]?.justification}
                            error={
                              !!(
                                touched !== undefined &&
                                touched[0]?.justification &&
                                errors?.[0]?.justification !== undefined &&
                                errors[0]?.justification !== ''
                              )
                            }
                            helperText={
                              touched !== undefined &&
                              touched[0]?.justification &&
                              errors?.[0]?.justification !== undefined &&
                              errors[0]?.justification
                            }
                            minRows={3}
                            multiline
                            required
                          />
                        </Box>
                      )}
                    </div>
                  );
                })}
              </Box>
            </Stack>
            <div className="flex py-4 justify-center">
              {!isViewMode && (
                <Button variant="contained" color="primary" type="submit">
                  {PAGE_TEXT.SAVE}
                </Button>
              )}
            </div>
          </form>
        }
      />
    </div>
  );
};
